/**
 * src/observability/events.ts
 *
 * Writer canônico de eventos de observabilidade.
 *
 * Regra invariante: nunca lançar exceção. Erros de escrita são logados via
 * console.error e ignorados — o caller não precisa try/catch.
 *
 * Estado atual: stub. _writeToDb apenas faz console.debug. A Task 5 do plano
 * `docs/superpowers/plans/2026-05-08-observabilidade.md` substitui o stub por
 * `database.action()` real após o schema v31 existir.
 */

import { Q } from '@nozbe/watermelondb'
import database from '../database'
import { store } from '../store'
import { buildEventContext } from './eventContext'
import { planTrim, LevelCounts } from './trimPlan'
import { resolveDeviceId } from './deviceIdentity'

// IDs precisam ser RFC 4122 v4 estritos (o backend valida com regex rígida e
// rejeita com `invalid_id`). `react-uuid` não preenche os bits de versão/variant,
// então usamos `crypto.randomUUID()` (nativo, v4 garantido). Fallback defensivo
// só se algum ambiente exótico não tiver crypto disponível.
function uuidV4(): string {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID()
  }
  // Fallback baseado em getRandomValues — mantém compliance com v4.
  const bytes = new Uint8Array(16)
  crypto.getRandomValues(bytes)
  bytes[6] = (bytes[6] & 0x0f) | 0x40 // versão 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80 // variant 10xx
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

// ─── Tipos ──────────────────────────────────────────────────────────────────

export type EventLevel = 'info' | 'warn' | 'error'

export type EventCategory =
  'sync' | 'auth' | 'iatf' | 'protocolo' | 'farm' | 'user' | 'outro'

export interface EventInput {
  level: EventLevel
  category: EventCategory
  action: string
  payload?: Record<string, unknown>
  userId?: string
  farmId?: string
}

interface InternalEventRecord {
  timestamp: number
  level: EventLevel
  category: EventCategory
  action: string
  payload: string
  userId?: string
  farmId?: string
  deviceId: string
}

// ─── Constantes ─────────────────────────────────────────────────────────────

const PAYLOAD_CAP_BYTES = 4 * 1024 // 4 KB
const EVENTS_MAX = 5000
const EVENTS_TARGET_AFTER_TRIM = 4900
const TRIM_CHECK_PROBABILITY = 1 / 50

// ─── deviceId ───────────────────────────────────────────────────────────────

/**
 * Identificador ESTÁVEL do aparelho/navegador: localStorage.deviceId (`dev_<uuid>`).
 * O GUID de sessão (window.name, mantido por verifyDuplicatePages.js) NÃO é mais o
 * deviceId: vai no payload como `sessionId` (ver eventContext). Ver deviceIdentity.ts.
 */
function getDeviceId(): string {
  try {
    return resolveDeviceId(
      typeof localStorage !== 'undefined' ? localStorage : null,
      uuidV4,
    )
  } catch {
    return 'unknown'
  }
}

// ─── Captura de contexto ────────────────────────────────────────────────────

/**
 * Extrai farmId da URL (rotas do tipo `/farms/:farmId/...`). É a fonte mais
 * confiável: navegação principal (`Home → farm card`) usa `history.push` sem
 * dispatchar `selectFarm`, então `state.farm.selected` fica null. Já a URL
 * sempre tem o id no segmento depois de `/farms/`.
 *
 * Exclui os segmentos conhecidos que NÃO são farmId:
 * - `/farms/create` (formulário de criar)
 * - `/farms/estacaodemonta` (página agregada multi-fazenda)
 */
function getFarmIdFromUrl(): string | undefined {
  if (typeof window === 'undefined') return undefined
  // pathname já não tem query/hash, mas o regex exclui `?` e `#` por garantia.
  const match = window.location.pathname.match(/^\/farms\/([^/?#]+)/)
  if (!match) return undefined
  const segment = match[1]
  if (segment === 'create' || segment === 'estacaodemonta') return undefined
  return segment
}

const URL_MAX_LEN = 200

/**
 * Pathname + search da URL atual. Hash omitido (client-side, raramente útil pra
 * diagnóstico). Hostname omitido (constante: o próprio app).
 * Capado em 200 chars como defesa contra query strings patológicos.
 */
function getCurrentUrl(): string {
  if (typeof window === 'undefined') return ''
  const url = window.location.pathname + window.location.search
  return url.length > URL_MAX_LEN ? url.slice(0, URL_MAX_LEN) + '…' : url
}

/**
 * Retorna o contexto do usuário/fazenda corrente.
 *
 * - userId: vem de `database.adapter.getLocal('LOGGED_USER_ID')` — fonte da
 *   verdade do login (mesma usada por `getLoggedUser` em redux/auth/actions).
 *   Redux `state.auth.logged` NUNCA é populado: o reducer trata `USER_LOGGED`
 *   mas o action nunca é despachado em lugar nenhum do código.
 * - farmId: prioridade URL (`/farms/:farmId/...`) → fallback Redux
 *   (`state.farm.selected.id`, populado pelo modal de troca em Dialog.js).
 *   Navegação principal não chama `selectFarm`, então a URL é o caminho real.
 *
 * Async por causa do adapter.getLocal (IndexedDB, mas LokiJS in-memory: sub-ms).
 */
async function getContext(): Promise<{ userId?: string; farmId?: string }> {
  let userId: string | undefined
  try {
    const value = await database.adapter.getLocal('LOGGED_USER_ID')
    userId = value ?? undefined
  } catch {
    /* ignore — userId fica undefined */
  }

  let farmId: string | undefined = getFarmIdFromUrl()
  if (!farmId) {
    try {
      const state: any = store.getState()
      farmId = state?.farm?.selected?.id ?? undefined
    } catch {
      /* ignore — farmId fica undefined */
    }
  }

  return { userId, farmId }
}

// ─── Serialização do payload com cap de 4 KB ────────────────────────────────

function serializePayload(payload?: Record<string, unknown>): string {
  if (!payload) return ''
  try {
    const serialized = JSON.stringify(payload)
    const size =
      typeof Blob !== 'undefined'
        ? new Blob([serialized]).size
        : serialized.length
    if (size <= PAYLOAD_CAP_BYTES) return serialized
    // Preserva o contexto mesmo quando o corpo estoura o cap.
    return JSON.stringify({
      _truncated: true,
      keys: Object.keys(payload),
      appVersion: payload.appVersion,
      sessionId: payload.sessionId,
      online: payload.online,
      netType: payload.netType,
    })
  } catch {
    return '{"_serializationError":true}'
  }
}

// ─── Retenção ───────────────────────────────────────────────────────────────

/**
 * Cap por quantidade. Verificação probabilística (1/50) para não pagar
 * `fetchCount` em cada escrita. Quando excede `EVENTS_MAX`, deleta os mais
 * antigos por `created_at` ASC até voltar a `EVENTS_TARGET_AFTER_TRIM`,
 * sacrificando por severidade: `info` → `warn` → `error` (ver `planTrim`).
 */
async function trimEventsIfNeeded(): Promise<void> {
  if (Math.random() > TRIM_CHECK_PROBABILITY) return

  try {
    const dropped = (await database.action(
      async (): Promise<LevelCounts | null> => {
        const collection: any = database.collections.get('events')
        const count: number = await collection.query().fetchCount()
        if (count <= EVENTS_MAX) return null

        const toDelete = count - EVENTS_TARGET_AFTER_TRIM
        const levels = ['info', 'warn', 'error'] as const
        const counts: LevelCounts = { info: 0, warn: 0, error: 0 }
        for (const lvl of levels) {
          counts[lvl] = await collection
            .query(Q.where('level', lvl))
            .fetchCount()
        }
        const plan = planTrim(toDelete, counts)

        const victims: any[] = []
        for (const lvl of levels) {
          if (plan[lvl] <= 0) continue
          const oldest: any[] = await collection
            .query(
              Q.where('level', lvl),
              Q.experimentalSortBy('created_at', Q.asc),
              Q.experimentalTake(plan[lvl]),
            )
            .fetch()
          victims.push(...oldest)
        }

        await database.batch(
          ...victims.map((r) => r.prepareDestroyPermanently()),
        )
        return plan
      },
    )) as LevelCounts | null
    // Um único aviso (fora do database.action), só se perdemos algo relevante.
    if (dropped && (dropped.warn > 0 || dropped.error > 0)) {
      void recordEvent({
        level: 'warn',
        category: 'outro',
        action: 'events_trimmed',
        payload: { dropped },
      })
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[events.trimEventsIfNeeded] failed', err)
  }
}

// ─── Writer interno ──────────────────────────────────────────────────────────

async function _writeToDb(record: InternalEventRecord): Promise<void> {
  await database.action(async () => {
    const collection: any = database.collections.get('events')
    await collection.create((entity: any) => {
      entity._raw.id = uuidV4()
      entity._raw.created_at = record.timestamp
      entity._raw.updated_at = record.timestamp
      entity.timestamp = record.timestamp
      entity.level = record.level
      entity.category = record.category
      entity.action = record.action
      entity.payload = record.payload
      entity.userId = record.userId ?? null
      entity.farmId = record.farmId ?? null
      entity.deviceId = record.deviceId
      entity.isDeleted = false
      entity.priority = false
    })
  })

  // Trim assíncrono — não bloqueia o caller. Erros já são logados internamente.
  void trimEventsIfNeeded()
}

// ─── Push imediato pra eventos de erro ──────────────────────────────────────

/**
 * Eventos `level: 'error'` precisam chegar no backend antes do usuário fechar
 * o tab. O ciclo normal do sync espera até 30s — tarde demais pra debug.
 *
 * - Debounce de 250ms coalesce rajadas (e.g., render loop emitindo N erros) em
 *   uma única chamada de pushEvents.
 * - Flag `pushScheduled` evita kicks concorrentes enquanto uma já está pending.
 * - Dynamic import quebra o ciclo events.ts ↔ syncWorker.js (syncWorker importa
 *   recordEvent estaticamente).
 * - Falhas são silenciadas — token ausente, erro de rede, ou qualquer outro
 *   problema caem aqui sem propagar.
 */
let pushScheduled = false

async function triggerImmediatePush(): Promise<void> {
  if (pushScheduled) return
  pushScheduled = true
  try {
    await new Promise((resolve) => setTimeout(resolve, 250))
    const [syncMod, authMod] = await Promise.all([
      import('../syncWorker'),
      import('../redux/auth/actions'),
    ])
    const auth = await authMod.getLoggedUser()
    if (!auth?.token) return
    await syncMod.pushEvents(auth.token)
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[events.triggerImmediatePush] failed', err)
  } finally {
    pushScheduled = false
  }
}

// ─── API pública ────────────────────────────────────────────────────────────

export async function recordEvent(input: EventInput): Promise<void> {
  try {
    const ctx = await getContext()
    // Enriquece o payload com `url` (pathname + search). Caller vence se passar
    // `url` explícito no payload (raríssimo). Sem `url`, capturado aqui.
    const enrichedPayload = {
      ...buildEventContext(),
      url: getCurrentUrl(),
      ...(input.payload ?? {}),
    }
    await _writeToDb({
      timestamp: Date.now(),
      level: input.level,
      category: input.category,
      action: input.action,
      payload: serializePayload(enrichedPayload),
      userId: input.userId ?? ctx.userId,
      farmId: input.farmId ?? ctx.farmId,
      deviceId: getDeviceId(),
    })

    // Eventos de erro disparam push imediato (não bloqueia o caller).
    if (input.level === 'error') {
      void triggerImmediatePush()
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[events.recordEvent] failed', err)
  }
}

export async function recordError(
  error: unknown,
  ctx: Omit<EventInput, 'level'>,
): Promise<void> {
  try {
    const err =
      error instanceof Error ? error : new Error(String(error ?? 'unknown'))
    await recordEvent({
      ...ctx,
      level: 'error',
      payload: {
        ...(ctx.payload ?? {}),
        message: err.message,
        stack: typeof err.stack === 'string' ? err.stack.slice(0, 500) : '',
      },
    })
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[events.recordError] failed', e)
  }
}

export async function recordSaveOutcome(
  category: EventCategory,
  entityName: string,
  outcome: 'success' | 'error',
  extra?: Record<string, unknown>,
): Promise<void> {
  try {
    await recordEvent({
      level: outcome === 'success' ? 'info' : 'error',
      category,
      action: `${entityName}_${outcome === 'success' ? 'saved' : 'save_failed'}`,
      payload: extra,
    })
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[events.recordSaveOutcome] failed', e)
  }
}

// ─── Debug hooks (dev only) ─────────────────────────────────────────────────
// Mesma convenção de window.__SYNC__ e window.__database__.
// Permite smoke-testar recordEvent direto do console em dev sem dynamic import.
//   await window.__OBS__.recordEvent({ level: 'info', category: 'outro', action: 'test' })
//   window.__OBS__.getCurrentUrl()
if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
  ;(window as any).__OBS__ = {
    recordEvent,
    recordError,
    recordSaveOutcome,
    getCurrentUrl,
    getFarmIdFromUrl,
  }
}
